import * as React from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
    CircularProgress,
    FormHelperText,
    Grid,
    Modal,
    Paper,
    Link,
    Button,
    CssBaseline,
    Box,
    Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

import "./Forget.css";
import { loginUser } from "Features/slice/userSlice";
import { RootState } from "Features/store";
import { getCurrentAppTheme } from "Features/slice/app/app.slice";
import { useAppDispatch, useAppSelector } from "Hooks/reduxHook";
import { LocalImage } from "Utils/images";
import { AppTheme } from "Utils/theme";
import { isValidString } from "Utils/validators";
import { Colors } from "Utils/colors";
import { Strings } from "Utils/strings";
import { CustomInput } from "Components/Form";

const defaultTheme = createTheme();

const initialValues = {
    email:"",
    username: "",
    password: "",
};

const signinSchema = Yup.object().shape({
    email: Yup.string()
    .email("Invalid email address") 
    .required("Email is required"),
    username: Yup.string().required("Username is required"),
    password: Yup.string().required("Password is required"),
});

export default function ForgetPassword() {


    const { errorMessage, hasError, isAuthenticating } = useAppSelector(
        (state: RootState) => state.user
    );
    const currentTheme = useAppSelector(getCurrentAppTheme);

    const dispatch = useAppDispatch();

    const forgetPasswordForm = useFormik({
        initialValues,
        validationSchema: signinSchema,
        validateOnMount: true,
        onSubmit: (values: typeof initialValues) => { },
    });

    const handleSubmit = (e: any) => {
        e.preventDefault();
        forgetPasswordForm
            .submitForm()
            .then(() => {
                if (forgetPasswordForm.isValid) {
                    dispatch(loginUser(forgetPasswordForm.values));
                } else {
                    alert("Check your form info");
                }
            })
            .catch((error) => {
                alert("Check your form info");
            });
    };

    const style = {
        position: "absolute" as "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
    };

    const passwordRef = React.useRef<any>(null);

    return (
        <ThemeProvider theme={defaultTheme}>
            <Grid
                container
                component="main"
                sx={{ height: "100vh" }}
                className="font-poppins"
            >
                <CssBaseline />
                <Grid
                    item
                    xs={false}
                    sm={false}
                    md={6}
                    lg={6}
                    sx={{
                        backgroundImage: `url(${LocalImage.contactBg})`,
                        backgroundRepeat: "no-repeat",
                        backgroundColor: `${Colors.primary}`,
                        backgroundSize: "80% 65%",
                        backgroundPosition: "right bottom",
                    }}
                />
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={6}
                    component={Paper}
                    elevation={6}
                    square
                    className="content-box"
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "left",
                            flex: 1,
                            my: 8,
                        }}
                    >
                        <img alt="WG Fabricators logo"
                            style={{ width: '12vw', height: '4vw' }}
                            src={LocalImage.newLogo} />
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "left",
                        }}
                    >
                        <Typography variant="h4" className="font-poppins font-poppins-thin">
                            {Strings.welcomeBack}
                        </Typography>
                        <Typography variant="h4" className="font-poppins font-poppins-bold">
                            {Strings.forgetPassword}
                        </Typography>
                    </Box>

                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        noValidate
                        sx={{ mt: 4, display: "flex", flexDirection: "column" }}
                    >
                        <CustomInput
                            type="email" 
                            errorMessage={forgetPasswordForm.errors.email as string} 
                            handleBlur={forgetPasswordForm.handleBlur("email")} 
                            isError={
                                (isValidString(forgetPasswordForm.errors.email) &&
                                forgetPasswordForm.touched.email) || 
                                false
                            }
                            onChangeText={forgetPasswordForm.handleChange("email")} 
                            required
                            size="medium"
                            value={forgetPasswordForm.values.email} 
                            label="Email" 
                            onSubmitEditing={() => passwordRef.current?.focus()}
                            placeholder="Email" 
                            inputClassName="font-poppins"
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2,
                                backgroundColor: AppTheme.color[currentTheme].secondaryColor,
                                textTransform: "none",
                            }}
                            onClick={handleSubmit}
                        >
                            {isAuthenticating ? "Logging In" : Strings.forgetPassword}
                        </Button>
                        {hasError && <FormHelperText error>{errorMessage}</FormHelperText>}
                    </Box>


                    <Modal
                        open={isAuthenticating}
                        aria-labelledby="modal-modal-title"
                        aria-describedby="modal-modal-description"
                    >
                        <Box sx={style}>
                            <CircularProgress />
                        </Box>
                    </Modal>
                </Grid>
            </Grid>
        </ThemeProvider>
    );
}
